<script setup lang="ts">
import { QCarousel, QCarouselSlide } from 'quasar'
import { computed, ref, watch } from 'vue'
import { Dayjs } from 'dayjs'
import { usePrevioDS } from '../../../composables/helpers'
import { useDateTimeRangePicker } from '../composables/DateTimeRangePickerState'
import { DateTimeRange } from '../model/DateTimeRange'
import PDivider from '../../divider/PDivider.vue'
import Calendar from './Calendar.vue'

type Props = {
  defaultTimeFrom?: string
  defaultTimeTo?: string
}

defineProps<Props>()

const model = defineModel<DateTimeRange>({ default: new DateTimeRange() })

const ds = usePrevioDS()
const qCarousalComponent = ref<InstanceType<typeof QCarousel>>()
const navigation = useDateTimeRangePicker()
const monthIndexes: number[] = Array.from({ length: 12 }, (_, i) => {
  return i
})

const chunkedSlides = computed<number[][]>(() => {
  const result = []

  if (ds.screen().isMobile) {
    for (const monthIndex of monthIndexes) {
      result.push([monthIndex])
    }
  } else {
    for (let i = 0; i < monthIndexes.length; i++) {
      const chunk = [monthIndexes[i], monthIndexes[(i + 1) % monthIndexes.length]]
      result.push(chunk)
    }
  }

  return result
})

const getSlideIndexByMonth = (month: number): number => {
  return chunkedSlides.value.findIndex((arr) => arr[0] === month)
}

const getMonthPairsByIndex = (index: number): number[] | null => {
  return chunkedSlides.value[index] || null
}

const isManualTransition = ref<boolean>(false)
const slideIndex = ref<number>(getSlideIndexByMonth(navigation.currentCalendar().month()))

const subtractMonth = (): void => {
  isManualTransition.value = true
  qCarousalComponent.value?.previous()
}

const advanceMonth = (): void => {
  isManualTransition.value = true
  qCarousalComponent.value?.next()
}

const navigateToMonth = (month: number): void => {
  if (!qCarousalComponent.value) {
    return
  }

  isManualTransition.value = true
  qCarousalComponent.value?.goTo(getSlideIndexByMonth(month))
}

const onTransition = (): void => {
  if (isManualTransition.value) {
    isManualTransition.value = false // Reset flag, ignore this transition
    return
  }

  const selectedPair = getMonthPairsByIndex(slideIndex.value)
  const currentMonth = navigation.currentCalendar().month()

  if (!selectedPair) {
    return
  }

  if (selectedPair[0] === 11 && currentMonth === 0) {
    // Swiping left from January (0) → Go to December (11)
    navigation.subtractMonth()
  } else if (selectedPair[0] === 0 && currentMonth === 11) {
    // Swiping right from December (11) → Go to January (0)
    navigation.advanceMonth()
  } else if (currentMonth <= selectedPair[0]) {
    // Regular forward swipe
    navigation.advanceMonth()
  } else {
    // Regular backward swipe
    navigation.subtractMonth()
  }
}

watch(
  () => navigation.currentCalendar(),
  (currentCalendar: Dayjs) => {
    navigateToMonth(currentCalendar.month())
  },
)

defineExpose({
  subtractMonth,
  advanceMonth,
})
</script>

<template>
  <q-carousel
    ref="qCarousalComponent"
    v-model="slideIndex"
    transition-prev="slide-right"
    transition-next="slide-left"
    class="ds-date-time-range-picker__carousel p-non-selectable"
    animated
    swipeable
    infinite
    @update:model-value="onTransition"
  >
    <q-carousel-slide
      v-for="(slide, index) in chunkedSlides"
      :key="index"
      :name="index"
      :disable="false"
      class="ds-date-time-range-picker__carousel-slide"
    >
      <div class="ds-date-time-range-picker__calendars">
        <template v-for="monthIndex in slide" :key="monthIndex">
          <calendar
            v-model="model"
            :month-index="monthIndex"
            :is-first-slide="slide.indexOf(monthIndex) === 0"
            :default-time-from="defaultTimeFrom"
            :default-time-to="defaultTimeTo"
          />
          <p-divider v-if="slide.indexOf(monthIndex) === 0 && slide.length > 1" vertical />
        </template>
      </div>
    </q-carousel-slide>
  </q-carousel>
</template>

<style scoped lang="scss">
@import '../../../assets/scss/shared/variables/ds.spacing';

.ds-date-time-range-picker {
  &__carousel {
    height: unset;
  }

  &__carousel-slide {
    padding: unset;
    overflow: hidden;
  }

  &__calendars {
    display: flex;
    gap: $space-sm-mobile;
  }
}
</style>
